import React, { useState, useEffect } from "react";
import { useAccounts, useAccountFilters } from "@/hooks";
import { useProviderOptions } from "@/hooks/useProviderOptions";
import ErrorMessage from "@/components/ui/ErrorMessage.jsx";
import LoadingSpinner from "@/components/ui/LoadingSpinner.jsx";
import DevelopmentBanner from "@/components/ui/DevelopmentBanner.jsx";
import ResultsCount from "@/components/molecules/ResultsCount.jsx";
import FiltersBar from "./FiltersBar.jsx";
import AccountsTable from "./AccountsTable.jsx";
import AccountDetailsModal from "./AccountDetailsModal.jsx";

function AccountManagementContent() {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const {
    accounts,
    loading,
    error,
    isMockData,
    loadAccounts,
    clearError,
    retry,
  } = useAccounts();

  const {
    filteredAccounts,
    filters,
    setSearchFilter,
    setCloudFilter,
    setStatusFilter,
    clearFilters,
    filterStats,
  } = useAccountFilters(accounts);

  const { providerOptions } = useProviderOptions(accounts);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const openModal = (account) => {
    setSelectedAccount(account);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAccount(null);
  };

  const handleModalClose = () => {
    closeModal();
  };

  const formatLabel = (key) => {
    const labelMap = {
      AccountName: "Account Name",
      AccountId: "Account ID",
      CloudProvider: "Cloud Provider",
      AccountOwner: "Account Owner",
      Accountable: "Accountable",
      OperationsResponsible: "Operations Responsible",
      BillingResponsible: "Billing Responsible ",
      BudgetOwner: "Budget Owner",
      AlternateContacts: "Alternate Contacts",
      DomainName: "Domain Name",
      Decommissioned: "Status",
      DecommissioningDate: "Decommissioning Date",
      ITDemandTicket: "IT Demand Ticket",
      BudgetLineReference: "Budget Line Reference",
      OpcoName: "Opco",
      Code: "Cost Center",
      AccountsGroupName: "Group"
    };
    return labelMap[key] || key;
  };

  if (loading && accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <LoadingSpinner message="Loading accounts..." />
      </div>
    );
  }

  if (error && accounts.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <ErrorMessage error={error} onRetry={retry} showRetry={true} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <DevelopmentBanner
        isVisible={isMockData || import.meta.env.VITE_USE_MOCK_DATA === "true"}
        message={
          isMockData
            ? "Using mock data (API unavailable)"
            : "Development Mode: Mock data enabled"
        }
        type={isMockData ? "warning" : "info"}
      />

      {error && accounts.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <ErrorMessage
            error={error}
            onRetry={clearError}
            showRetry={false}
            className="warning-message"
          />
        </div>
      )}

      <FiltersBar
        searchValue={filters.search}
        onSearchChange={(e) => setSearchFilter(e.target.value)}
        cloudValue={filters.cloud}
        onCloudChange={(e) => setCloudFilter(e.target.value)}
        statusValue={filters.status}
        onStatusChange={(e) => setStatusFilter(e.target.value)}
        onClear={clearFilters}
        providerOptions={providerOptions}
      />

      {filterStats.filtered !== filterStats.total && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-blue-800 font-medium">
              Showing {filterStats.filtered} of {filterStats.total} accounts
            </span>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Clear Filters
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Account Management
            </h2>
            <ResultsCount
              filtered={filterStats.filtered}
              total={filterStats.total}
              loading={loading}
            />
          </div>
        </div>
        <div className="p-4">
          <AccountsTable
            accounts={filteredAccounts}
            onViewAccount={openModal}
          />
          {filteredAccounts.length === 0 && filterStats.total > 0 && (
            <p className="text-center text-gray-500 mt-4">
              No accounts found with applied filters.
            </p>
          )}
          {filterStats.total === 0 && !loading && (
            <p className="text-center text-gray-500 mt-4">
              No registered accounts.
            </p>
          )}
        </div>
      </div>

      <AccountDetailsModal
        account={selectedAccount}
        open={showModal}
        onClose={handleModalClose}
        formatLabel={formatLabel}
      />
    </div>
  );
}

export default AccountManagementContent;
